import React, { useContext, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { UserContext } from "../../HomeApp"
import { openAuthDialog } from "../../Authentication"
import TaggableInput from "../../shared/fields/TaggableInput"
import ImagesUpload from "../../ImagesUpload"
import { toast } from "react-toastify"
import { useMutation } from "react-query"
import { mutationRequest } from "@util/request"
import { uploadImages } from "../../../util/backend_api/posts"
import { convertHeicToJpeg } from "../../../util/heicConversion"
import { Comment } from "../../../requests/comments"
import { CardImage } from "react-bootstrap-icons"
import { ImagePreview } from "../../../types/posts"

interface CommentFormProps {
  postId: number
  parentId?: number | null
  parentUsername?: string | null
  placeholder?: string
  onSuccess?: (comment: Comment | null) => void
  onCancel?: (() => void) | null
}

const CommentForm: React.FC<CommentFormProps> = ({
  postId,
  parentId = null,
  parentUsername = null,
  placeholder = "Kick off the conversation...",
  onSuccess = () => {},
  onCancel = null,
}) => {
  const { user } = useContext(UserContext)
  const dispatch = useDispatch()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [imagePreviews, setImagePreviews] = useState<ImagePreview[]>([])
  const [imageFiles, setImageFiles] = useState<File[]>([])
  const [commentText, setCommentText] = useState("")
  const [, setUserTags] = useState<unknown[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const mutation = useMutation(mutationRequest)

  const resetForm = () => {
    setCommentText("")
    setImageFiles([])
    setImagePreviews([])
    setUserTags([])
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  const submitComment = async (e: React.MouseEvent | React.FormEvent) => {
    e.preventDefault()
    if (!commentText.trim() && imagePreviews.length === 0) return
    setIsSubmitting(true)

    try {
      let signedIds: string[] = []
      if (imagePreviews.length > 0) {
        const uploadResponse = await uploadImages(
          imagePreviews,
          setImagePreviews,
        )
        if (uploadResponse.errors.length > 0) {
          toast.error(
            "Sorry, there was an error uploading your images. Please try again.",
          )
          setIsSubmitting(false)
          return
        }
        signedIds = uploadResponse.signedIds
      }

      const text = commentText
      const commentData: Record<string, unknown> = {
        comment_text: text,
        images: signedIds,
      }
      if (parentId) {
        commentData.parent_id = parentId
      }
      const response = await mutation.mutateAsync({
        options: { method: "POST" },
        path: `/api/v1/posts/${postId}/comments`,
        data: { comment: commentData },
      })
      onSuccess(response?.comment ?? null)
      resetForm()
    } catch (e) {
      console.error("Failed to submit: ", e)
    }

    setIsSubmitting(false)
  }

  const inputPlaceholder = parentUsername
    ? `Reply to ${parentUsername}...`
    : placeholder

  return user ? (
    <div className="border border-gray-200 rounded-sm bg-white mb-4">
      <div className="px-1 pt-1">
        <ImagesUpload
          imageFiles={imageFiles}
          setImageFiles={setImageFiles}
          imagePreviews={imagePreviews}
          setImagePreviews={setImagePreviews}
          triggerRef={fileInputRef}
          hideTriggerButton={true}
          maxFiles={4}
          preprocessFile={convertHeicToJpeg}
        />
      </div>

      <div className="px-1 pt-1">
        <TaggableInput
          value={commentText}
          onChange={setCommentText}
          placeholder={inputPlaceholder}
          autoFocus={!!parentId}
          className="comment-screen"
        />
      </div>

      <div className="flex items-center justify-between px-3 py-2">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="text-gray-400 hover:text-gray-600 focus:outline-none disabled:opacity-30"
          aria-label="Attach images"
          disabled={isSubmitting || imagePreviews.length >= 4}
        >
          <CardImage size={18} />
        </button>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={isSubmitting}
            onClick={() => {
              if (onCancel) {
                onCancel()
              } else {
                resetForm()
              }
            }}
            className="text-sm px-3 py-1 rounded-sm border border-gray-400 text-gray-600 hover:border-gray-600 hover:text-gray-800 focus:outline-none transition-colors disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={
              isSubmitting ||
              (!commentText.trim() && imagePreviews.length === 0)
            }
            onClick={submitComment}
            className="text-sm px-3 py-1 rounded-sm bg-gray-800 text-white disabled:opacity-40 hover:bg-gray-700 focus:outline-none transition-colors"
          >
            {isSubmitting ? "Posting..." : "Post Comment"}
          </button>
        </div>
      </div>
    </div>
  ) : (
    <textarea
      className="border w-full px-3 py-2 border-gray-200 rounded-sm h-12 focus:outline-none"
      placeholder="You must be logged in to participate in the comments"
      onFocus={(e) => {
        e.target.blur()
        dispatch(
          openAuthDialog({
            explanation:
              "You must first sign up or log in to participate in the comments",
          }),
        )
      }}
    />
  )
}

export default CommentForm
